import React, { FC } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { compose } from '@reduxjs/toolkit';

import type { BettingContextProps } from '../../types';
import type { ReduxState } from 'app/redux/types';

import { withinBettingContext } from 'app/contexts/betting';
import { Icon, IconName } from 'app/design-system/components/Icon';
import { SportId } from 'app/lib/betting/constants';
import { getSportKey } from 'app/lib/betting/sport-utils';
import { composeSportPath, getPrefixByNamespace } from 'app/modules/Navigation/utils';

import ColumnsHeader from '../ColumnsHeader';

import styles from './GridHeader.module.scss';

// ---

type StoreProps = {
  eventName: string;
  namespace: string;
};

type GridHeaderProps = {
  sportId: SportId;
  eventName?: string;
  className?: string;
  columnsHeaderClassName?: string;
  showColumns?: boolean;
  shouldBeLink?: boolean;
};

const GridHeader: FC<GridHeaderProps & StoreProps> = ({
  sportId,
  namespace,
  shouldBeLink,
  eventName,
  className,
  columnsHeaderClassName,
  showColumns = true,
}) => {
  const content = (
    <>
      <Icon
        className={styles.icon}
        icon={`sport-${getSportKey(sportId)}` as IconName}
        size={24} />
      {eventName}
    </>
  );

  return (
    <div data-test="grid-header"
         className={classNames(styles.gridHeader, className)}>
      {shouldBeLink ? (
        <Link className={styles.name}
              to={composeSportPath({ sportId }, getPrefixByNamespace(namespace))}>
          {content}
        </Link>
      ) : (
        <p className={styles.name}>{content}</p>
      )}

      {showColumns && (
        <div className={styles.gridColumnsHeader}>
          <ColumnsHeader sportId={sportId}
                         className={columnsHeaderClassName} />
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: ReduxState, {
  $betting: {
    namespace,
    content: {
      selectors: { sports },
    },
  },
  sportId,
  eventName,
}: BettingContextProps & GridHeaderProps): StoreProps => {
  if (eventName) {
    return {
      eventName,
      namespace,
    };
  }

  const sport = sports(state).get(sportId);

  return ({
    namespace,
    eventName: sport && sport.name,
  });
};

export default compose<FC<GridHeaderProps>>(
  withinBettingContext,
  connect(mapStateToProps),
)(GridHeader);
